import { Accordion, Button, Group, Select } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import { useState } from "react";
import { useAdvancePayments } from "../../../../../../hooks/useAdvancePayments";
import { type Delivery } from "../../../../../../utils/backend-types";
import type { InstallmentConfiguration } from "../../../../DeliveryOverview.types";
import { InstallmentElement } from "./InstallmentElement/InstallmentElement";
import "./InstallmentsTab.scss";

const ALL_YEARS = "ALL_YEARS";

interface InstallmentsTabProps {
  projectId: string;
  beginYear: number;
  contractId: string;
  deliveries: Delivery[];
  installmentsData: Array<InstallmentConfiguration>;
  estimatedInstallmentBlocked?: boolean;
  updateInstallments: (installments: Array<InstallmentConfiguration>) => void;
  addInstallment: () => InstallmentConfiguration;
}

function InstallmentsTab({
  beginYear,
  contractId,
  projectId,
  deliveries,
  installmentsData,
  updateInstallments,
  addInstallment
}: InstallmentsTabProps) {
  const [yearFilter, setYearFilter] = useState<number | null>(null);
  const { data: advancePaymentsPlans } = useAdvancePayments(projectId);

  const [openedElements, setOpenedElements] =
    useState<string[]>(getInitialOpenYears);

  const estimatedConsumptionBlocked = deliveries.some(
    (delivery) =>
      delivery.gridUsageBillingModel === "rlm" || delivery.useSpotMarketPrices
  );
  function advancePaymentsPlanAlreadyExists(year: number) {
    const advancePaymentPlan = advancePaymentsPlans?.find(
      (plan) =>
        (new Date(
          plan.advancePaymentsPlanData.paymentPlanBegin
        ).getFullYear() === year ||
          new Date(
            plan.advancePaymentsPlanData.paymentPlanEnd
          ).getFullYear() === year) &&
        plan.advancePaymentsPlanData.contract === contractId
    );
    return !!advancePaymentPlan;
  }

  const allYears = {
    value: ALL_YEARS,
    label: "Alle Jahre"
  };
  const years = installmentsData
    ? Array.from(new Set(installmentsData.map((pd) => pd.year)))
        .sort((a, b) => b - a)
        .map((year) => ({
          value: year.toString(),
          label: year.toString()
        }))
    : [];
  const allSelectItems = [allYears].concat(years);

  function handleChangeInstallments(installment: InstallmentConfiguration) {
    const index = installmentsData?.findIndex(
      (pd) => pd.year === installment.year
    );
    if (index !== undefined && index >= 0 && installmentsData) {
      const newInstallmentsData = [...installmentsData];
      newInstallmentsData[index] = installment;
      updateInstallments(newInstallmentsData);
    }
  }
  const filteredInstallmentsData: Array<InstallmentConfiguration> =
    installmentsData
      ? yearFilter
        ? installmentsData.filter((pd) => pd.year === yearFilter)
        : installmentsData
      : [];

  function getInitialOpenYears() {
    const installmentYears = installmentsData?.map((i) => i.year);
    const currentYear = new Date().getFullYear();
    let defaultYear = currentYear;
    if (
      installmentYears &&
      installmentYears.length > 0 &&
      !installmentYears.includes(currentYear)
    ) {
      defaultYear = installmentYears[0];
    }

    return [defaultYear.toString()];
  }

  return (
    <div className="InstallmentsTab">
      <Group justify="space-between">
        <Select
          data={allSelectItems}
          defaultValue={ALL_YEARS}
          onChange={(value) =>
            setYearFilter(value && value !== ALL_YEARS ? parseInt(value) : null)
          }
        />
        <Button
          onClick={() => {
            const newInstallment = addInstallment();
            setOpenedElements((prev) => [
              ...prev,
              newInstallment.year.toString()
            ]);
          }}
        >
          Eintrag hinzufügen
        </Button>
      </Group>
      <div className="installments-tab-content">
        <Accordion
          chevron={<IconChevronRight size={26} />}
          multiple
          value={openedElements}
          onChange={(value) => setOpenedElements(value)}
        >
          {filteredInstallmentsData
            ?.sort((a, b) => b.year - a.year)
            .map((installment) => {
              return (
                <InstallmentElement
                  advancePaymentsPlanAlreadyExists={advancePaymentsPlanAlreadyExists(
                    installment.year
                  )}
                  contractId={contractId}
                  estimatedConsumptionBlocked={estimatedConsumptionBlocked}
                  historyInstallmentsBlocked={installment.year <= beginYear}
                  installment={installment}
                  isOpen={openedElements.includes(installment.year.toString())}
                  key={installment.year}
                  onChange={(installment: InstallmentConfiguration) =>
                    handleChangeInstallments(installment)
                  }
                />
              );
            })}
        </Accordion>
      </div>
    </div>
  );
}

export { InstallmentsTab };
